import { debounce } from 'lodash';
import { useRef } from 'react';
import { queryCache, QueryKey, useMutation, useQuery } from 'react-query';
import executeApiRequest from '../utils/executeApiRequest.util';
import fetchAllPages from '../utils/fetchAllPages.util';
import { Endpoint } from './endpoints';

type ApiCallOptions = {
  pathParams?: Record<string, string>;
  queryParams?: Record<string, string | number | boolean>;
  data?: any;
  debounceKey?: string;
};

export type BaseServiceConfig<T> = {
  queryKey: QueryKey[];
  endpoints: {
    get?: Endpoint;
    create?: Endpoint;
    update?: Endpoint;
    delete?: Endpoint;
    order?: Endpoint;
    clone?: Endpoint;
  };
  defaultUrlParams?: Record<string, string | number | boolean>;
  enabled?: boolean;
  fetchAllPages?: boolean;
};

const operationConfig = {
  create: { endpoint: 'create', errorMessage: 'Create endpoint not configured' },
  update: { endpoint: 'update', errorMessage: 'Update endpoint not configured' },
  remove: { endpoint: 'delete', errorMessage: 'Delete endpoint not configured' },
  order: { endpoint: 'order', errorMessage: 'Order endpoint not configured' },
  clone: { endpoint: 'clone', errorMessage: 'Order endpoint not configured' },
};

export function useBaseService<T extends Record<string, any>>(config: BaseServiceConfig<T>, id?: string) {
  const queryKey = id ? [...config.queryKey, id] : config.queryKey;
  const pendingMutations = useRef(0);

  const invalidate = async (): Promise<void> => {
    pendingMutations.current -= 1;
    if (pendingMutations.current <= 0) {
      pendingMutations.current = 0;
      await queryCache.invalidateQueries(queryKey);
    }
    return;
  };

  const startMutation = () => {
    pendingMutations.current += 1;
  };

  const handleError = () => {
    pendingMutations.current -= 1;
  };

  const { data, isLoading, error } = useQuery(
    queryKey,
    async () => {
      if (!config.endpoints.get) {
        throw new Error('Get endpoint not configured');
      }
      
      if (config.fetchAllPages) {
        return fetchAllPages({
          endpoint: config.endpoints.get,
          id: id || '',
          urlParams: config.defaultUrlParams,
        });
      }
      
      return executeApiRequest({
        endpoint: config.endpoints.get,
        id: id || '',
        urlParams: config.defaultUrlParams,
      });
    },
    {
      enabled: config.enabled !== false,
    }
  );

  const useCreateMutation = (operation: keyof typeof operationConfig) => {
    const { endpoint, errorMessage } = operationConfig[operation];

    return useMutation(
      async (options: ApiCallOptions = {}) => {
        startMutation();
        if (!config.endpoints[endpoint]) {
          throw new Error(errorMessage);
        }

        const { pathParams, queryParams, data: payload } = options;

        return executeApiRequest({
          endpoint: config.endpoints[endpoint],
          id: pathParams?.id || id || '',
          urlParams: { ...config.defaultUrlParams, ...queryParams },
          payload: operation !== 'remove' ? payload : undefined,
        });
      },
      {
        onSuccess: invalidate,
        onError: handleError,
      }
    );
  };

  const [create] = useCreateMutation('create');
  const [update] = useCreateMutation('update');
  const [remove] = useCreateMutation('remove');
  const [order] = useCreateMutation('order');
  const [clone] = useCreateMutation('clone');
  const liveData = () => queryCache.getQueryData(queryKey);

  // Create a debounced update function that uses a cache to track separate debounce timers
  // for different update operations based on path params
  // tslint:disable-next-line:ban-types
  const debouncedUpdateCache = useRef<Record<string, Function>>({});

  const debouncedUpdate = (options: ApiCallOptions = {}) => {
    // Create a unique key based on path params and optional debounceKey
    let cacheKey = options.pathParams
      ? Object.entries(options.pathParams)
          .map(([key, value]) => `${key}:${value}`)
          .join('|')
      : 'default';

    // Append debounceKey if provided
    if (options.debounceKey) {
      cacheKey = `${cacheKey}|${options.debounceKey}`;
    }

    // Create a debounced function for this specific key if it doesn't exist
    if (!debouncedUpdateCache.current[cacheKey]) {
      debouncedUpdateCache.current[cacheKey] = debounce((opts: ApiCallOptions) => {
        // Remove the debounceKey from options before passing to update
        const { debounceKey, ...updateOptions } = opts;
        return update(updateOptions);
      }, 500);
    }

    // Call the appropriate debounced function
    return debouncedUpdateCache.current[cacheKey](options);
  };

  return {
    id,
    data,
    isLoading,
    error: error instanceof Error ? error : null,
    create,
    update,
    debouncedUpdate,
    delete: remove,
    order,
    invalidate,
    clone,
    liveData,
  };
}
